import React, { ReactNode, useMemo } from 'react'
import styled from 'styled-components'

import { Quad, safePtrAccess, usePRFile } from '@sketch-hq/sketch-web-renderer'
import { Rect } from '@sketch/inspector'
import { relativeMeasure } from '@sketch/utils'

import { cornersOf, hasMoved } from '../../utils'

import { OtherVersionOutline, placeQuad } from '../OtherVersionOutline'

const Outline = styled.div`
  position: absolute;
  pointer-events: none;

  /* This version's, whatever the swap draws in it, as a frame's outline is */
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.sketch.A};
`

/**
 * Holds what can be asked of the layer, under it rather than inside the
 * outline: the outline turns with the layer, and a menu on its side is not a
 * menu. Placed from the box that wraps it, so it clears a rotated one.
 */
const Footer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;

  /* The band is wider than the controls in it, which ask for them back */
  pointer-events: none;
`

/** Under the box that wraps the layer, whichever way the layer is turned. */
const placeFooter = (quad: Quad, rootExtent: Rect) => {
  const corners = cornersOf(quad)
  const left = Math.min(...corners.map(({ x }) => x))
  const right = Math.max(...corners.map(({ x }) => x))
  const bottom = Math.max(...corners.map(({ y }) => y))

  return {
    left: `${relativeMeasure(left, rootExtent.width)}%`,
    top: `${relativeMeasure(bottom, rootExtent.height)}%`,
    width: `${relativeMeasure(right - left, rootExtent.width)}%`,
  }
}

interface SelectedLayerOutlineProps {
  /** The layer picked from the changes list. */
  layerPathHash: string
  /**
   * Where the other version of it belongs, or null when the two versions do not
   * share it. Measured in the comparison, which holds both trees and so is the
   * one place that can put one version under the other's parent.
   */
  otherVersion: Quad | null
  /** The scene root the position is taken as a percentage of. */
  rootExtent: Rect
  /** Said under the layer: what can be asked of this change. */
  footer?: ReactNode
}

/**
 * Outlines the changed layer picked from the list, and where the other version
 * of it belongs when that is somewhere else - a layer that moved or was resized
 * sits in two places across the two versions.
 *
 * Both come as four corners rather than boxes, so a rotated layer is outlined
 * as the shape it is and not as the larger square around it.
 */
export function SelectedLayerOutline({
  layerPathHash,
  otherVersion,
  rootExtent,
  footer,
}: SelectedLayerOutlineProps) {
  const prFile = usePRFile()

  const quad = useMemo(() => {
    // The renderer may have deleted it since it was read; using it would throw
    const file = safePtrAccess(prFile)
    const node = file?.findByLayerPathHash(layerPathHash)

    // Its own corners: getAbsoluteBounds() grows to whatever overflows it, and
    // getAbsoluteLayerQuad() keeps the layer's turn rather than wrapping it
    return node?.getAbsoluteLayerQuad() ?? null
  }, [layerPathHash, prFile])

  if (!quad || !rootExtent.width || !rootExtent.height) return null

  // Only when it is somewhere else: a second outline over the first one would
  // just recolour the selection, which says the wrong thing about what is there
  const elsewhere =
    otherVersion && hasMoved(quad, otherVersion) ? otherVersion : null

  const placed = placeQuad(quad, rootExtent)
  if (!placed) return null

  return (
    <>
      {/* Where it belongs in the version not on screen, which is also where a
      swap draws it */}
      {elsewhere && (
        <OtherVersionOutline quad={elsewhere} rootExtent={rootExtent} />
      )}

      <Outline data-testid="compare-selected-layer-outline" style={placed} />

      {footer && (
        <Footer style={placeFooter(quad, rootExtent)}>{footer}</Footer>
      )}
    </>
  )
}
